import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RuleTestModal = ({ rule, isOpen, onClose, className = '' }) => {
  const [testInputs, setTestInputs] = useState(['']);
  const [testResults, setTestResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen || !rule) return null;

  const addTestInput = () => {
    setTestInputs(prev => [...prev, '']);
  };

  const removeTestInput = (index) => {
    if (testInputs?.length > 1) {
      setTestInputs(prev => prev?.filter((_, i) => i !== index));
      setTestResults(prev => prev?.filter((_, i) => i !== index));
    }
  };

  const handleInputChange = (index, value) => {
    const newInputs = [...testInputs];
    newInputs[index] = value;
    setTestInputs(newInputs);
  };

  const runTests = async () => {
    setIsLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const results = testInputs?.map((input, index) => {
      if (!input?.trim()) {
        return {
          input,
          matches: false,
          confidence: 0,
          matchedPattern: null,
          reason: 'Empty input'
        };
      }

      // Mock testing logic based on rule configuration
      let matches = false;
      let confidence = 0;
      let matchedPattern = null;
      let reason = '';

      rule?.patterns?.forEach(pattern => {
        if (rule?.patternType === 'exact') {
          const isMatch = rule?.caseSensitive 
            ? input === pattern 
            : input?.toLowerCase() === pattern?.toLowerCase();
          
          if (isMatch) {
            matches = true;
            confidence = 1.0;
            matchedPattern = pattern;
            reason = 'Exact match found';
          }
        } else if (rule?.patternType === 'regex') {
          try {
            const regex = new RegExp(pattern, rule.caseSensitive ? 'g' : 'gi');
            if (regex?.test(input)) {
              matches = true;
              confidence = 0.95;
              matchedPattern = pattern;
              reason = 'Regex pattern matched';
            }
          } catch (error) {
            reason = 'Invalid regex pattern';
          }
        } else {
          // Fuzzy matching
          const similarity = calculateSimilarity(
            input?.toLowerCase(), 
            pattern?.toLowerCase()
          );
          
          if (similarity >= rule?.confidenceThreshold) {
            matches = true;
            confidence = similarity;
            matchedPattern = pattern;
            reason = `Fuzzy match (${Math.round(similarity * 100)}% similarity)`;
          }
        }
      });

      if (!matches) {
        reason = `No patterns matched (threshold: ${Math.round(rule?.confidenceThreshold * 100)}%)`;
      }

      return {
        input,
        matches,
        confidence,
        matchedPattern,
        reason
      };
    });

    setTestResults(results);
    setIsLoading(false);
  };

  const calculateSimilarity = (str1, str2) => {
    const longer = str1?.length > str2?.length ? str1 : str2;
    const shorter = str1?.length > str2?.length ? str2 : str1;
    
    if (longer?.length === 0) return 1.0;
    
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer?.length - editDistance) / longer?.length;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2?.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1?.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2?.length; i++) {
      for (let j = 1; j <= str1?.length; j++) {
        if (str2?.charAt(i - 1) === str1?.charAt(j - 1)) {
          matrix[i][j] = matrix?.[i - 1]?.[j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix?.[i - 1]?.[j - 1] + 1,
            matrix?.[i]?.[j - 1] + 1,
            matrix?.[i - 1]?.[j] + 1
          );
        }
      }
    }
    return matrix?.[str2?.length]?.[str1?.length];
  };

  const clearAll = () => {
    setTestInputs(['']);
    setTestResults([]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className={`relative bg-card border border-border rounded-lg shadow-moderate max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Test Matching Rule</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Rule: <span className="font-medium">{rule?.name}</span> → {rule?.vendorName}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Rule Information */}
          <div className="mb-6 p-4 bg-muted/30 rounded-lg">
            <h3 className="text-sm font-medium text-foreground mb-3">Rule Configuration</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Pattern Type:</span>
                <span className="ml-2 font-medium text-foreground capitalize">{rule?.patternType}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Confidence Threshold:</span>
                <span className="ml-2 font-medium text-foreground">{Math.round(rule?.confidenceThreshold * 100)}%</span>
              </div>
              <div>
                <span className="text-muted-foreground">Case Sensitive:</span>
                <span className="ml-2 font-medium text-foreground">{rule?.caseSensitive ? 'Yes' : 'No'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Priority:</span>
                <span className="ml-2 font-medium text-foreground">{rule?.priority}</span>
              </div>
            </div>
            
            <div className="mt-3">
              <span className="text-muted-foreground text-sm">Patterns:</span>
              <div className="mt-1 space-y-1">
                {rule?.patterns?.map((pattern, index) => (
                  <code key={index} className="block px-2 py-1 bg-background rounded text-xs font-mono">
                    {pattern}
                  </code>
                ))}
              </div>
            </div>
          </div>

          {/* Test Inputs */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-foreground">Test Vendor Names</h3>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addTestInput}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Test
                </Button>
                {testResults?.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAll}
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {testInputs?.map((input, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Input
                      type="text"
                      placeholder="Enter vendor name to test"
                      value={input}
                      onChange={(e) => handleInputChange(index, e?.target?.value)}
                      className="flex-1"
                    />
                    {testInputs?.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTestInput(index)}
                        iconName="Trash2"
                      />
                    )}
                  </div>

                  {/* Test Result */}
                  {testResults?.[index] && (
                    <div className={`
                      p-3 rounded-md border text-sm
                      ${testResults?.[index]?.matches 
                        ? 'bg-success/10 border-success/20' :'bg-error/10 border-error/20'
                      }
                    `}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Icon 
                            name={testResults?.[index]?.matches ? "CheckCircle" : "XCircle"} 
                            size={16}
                            className={testResults?.[index]?.matches ? 'text-success' : 'text-error'}
                          />
                          <span className={`font-medium ${testResults?.[index]?.matches ? 'text-success' : 'text-error'}`}>
                            {testResults?.[index]?.matches ? 'Match Found' : 'No Match'}
                          </span>
                        </div>
                        
                        {testResults?.[index]?.matches && (
                          <span className="text-xs font-mono px-2 py-1 bg-background/50 rounded">
                            {Math.round(testResults?.[index]?.confidence * 100)}% confidence
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 text-xs">
                        <p><span className="text-muted-foreground">Reason:</span> {testResults?.[index]?.reason}</p>
                        {testResults?.[index]?.matchedPattern && (
                          <p>
                            <span className="text-muted-foreground">Matched Pattern:</span> 
                            <code className="ml-1 px-1 py-0.5 bg-background/50 rounded">
                              {testResults?.[index]?.matchedPattern}
                            </code>
                          </p>
                        )}
                        {testResults?.[index]?.matches && (
          <p><span className="text-muted-foreground">Would assign to:</span> {rule?.vendorName}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {testResults?.length > 0 && (
              <span>
                {testResults?.filter(r => r?.matches)?.length} of {testResults?.length} tests passed
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
            
            <Button
              variant="default"
              onClick={runTests}
              loading={isLoading}
              disabled={!testInputs?.some(input => input?.trim())}
              iconName="Play"
              iconPosition="left"
            >
              Run Tests
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleTestModal;
